import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { EASE } from "@/components/Reveal";

const Tile = ({ item, index, dark, imgAspect = "aspect-square", carousel = false, still = false }) => {
  const Wrap = still ? "div" : motion.div;
  const motionProps = still
    ? {}
    : {
        initial: { opacity: 0, y: 40 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" },
        transition: { duration: 0.7, ease: EASE, delay: (index % 3) * 0.08 },
      };
  const inner = (
    <Wrap {...motionProps} className="group relative h-full flex flex-col">
      <div className={`relative overflow-hidden ${imgAspect} bg-neutral-200`}>
        <img
          src={item.img}
          alt={item.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
        <span className="absolute top-4 left-4 font-head text-xs font-black tracking-widest text-white/80 mix-blend-difference">
          {item.n}
        </span>
        {item.link && (
          <span className="absolute top-4 right-4 bg-[var(--orange)] text-white p-2">
            <ArrowUpRight size={16} />
          </span>
        )}
      </div>
      <div className="pt-5 pb-8 px-1">
        <h4 className="font-head text-lg sm:text-xl font-bold flex items-center gap-2 group-hover:text-[var(--orange)] transition-colors duration-300">
          {item.title}
        </h4>
        <p
          className={`mt-2 text-sm leading-relaxed ${
            dark ? "text-white/60" : "text-black/60"
          }`}
        >
          {item.desc}
        </p>
      </div>
    </Wrap>
  );

  const wrapCls = `border-b border-r p-4 sm:p-6 ${
    dark ? "border-[var(--skunk-border)]" : "border-[var(--cerra-border)]"
  } ${carousel ? "w-[80%] shrink-0 snap-start snap-always sm:w-auto" : ""}`;

  return item.link ? (
    <Link data-testid={`tile-${item.title.toLowerCase().replace(/\W+/g, "-")}`} to={item.link} className={wrapCls}>
      {inner}
    </Link>
  ) : (
    <div data-testid={`tile-${item.title.toLowerCase().replace(/\W+/g, "-")}`} className={wrapCls}>
      {inner}
    </div>
  );
};

const ServiceSection = ({ id, kicker, title, subtitle, items, dark = false, imgAspect, carousel = false }) => {
  const border = dark ? "border-[var(--skunk-border)]" : "border-[var(--cerra-border)]";
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || !el.children.length) return;
    const w = el.children[0].offsetWidth;
    const max = el.scrollWidth - el.clientWidth;
    const idx = el.scrollLeft >= max - 4 ? items.length - 1 : Math.round(el.scrollLeft / w);
    setActive(idx);
  };

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: el.children[i].offsetLeft - el.children[0].offsetLeft, behavior: "smooth" });
  };

  const containerCls = carousel
    ? `flex overflow-x-auto overflow-y-hidden overscroll-x-contain snap-x snap-mandatory hide-scrollbar border-t border-l ${border} sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible`
    : `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l ${border}`;

  return (
    <section
      id={id}
      data-testid={`section-${id}`}
      className={`py-24 sm:py-32 px-5 sm:px-8 ${
        dark ? "bg-[var(--skunk-bg)] text-white grain relative" : "bg-white text-[var(--cerra-fg)]"
      }`}
    >
      <div className="mx-auto max-w-[1600px] relative z-10">
        <div className="flex flex-col gap-4 mb-14 border-b pb-8 border-current/10">
          <div>
            {kicker && (
              <span className="text-[var(--orange)] text-xs font-bold uppercase tracking-[0.22em]">
                ✦ {kicker}
              </span>
            )}
            <h2 className="mt-4 font-head text-3xl sm:text-4xl lg:text-5xl font-medium uppercase kern-tight leading-none">
              {title}
            </h2>
          </div>
          {subtitle && (
            <p className={`max-w-md text-sm ${dark ? "text-white/60" : "text-black/60"}`}>
              {subtitle}
            </p>
          )}
          {carousel && (
            <div
              data-testid={`scroll-hint-${id}`}
              className="sm:hidden mt-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--orange)]"
            >
              <span>Swipe to explore</span>
              <ArrowRight size={14} className="animate-nudge" />
            </div>
          )}
        </div>

        <div
          ref={trackRef}
          onScroll={carousel ? onScroll : undefined}
          data-testid={`track-${id}`}
          className={containerCls}
          style={carousel ? { touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" } : undefined}
        >
          {items.map((item, i) => (
            <Tile
              key={item.title}
              item={item}
              index={i}
              dark={dark}
              imgAspect={imgAspect}
              carousel={carousel}
              still={carousel && isMobile}
            />
          ))}
        </div>

        {carousel && (
          <div data-testid={`dots-${id}`} className="sm:hidden mt-6 flex items-center justify-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.title}
                type="button"
                aria-label={`Show ${item.title}`}
                data-testid={`dot-${id}-${i}`}
                onClick={() => goTo(i)}
                className={`h-2 transition-[width,background-color] duration-300 ${
                  i === active
                    ? "w-6 bg-[var(--orange)]"
                    : `w-2 ${dark ? "bg-white/25" : "bg-black/20"}`
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceSection;
